import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import axios from 'axios';
import {
  MdOutlinePalette, MdOutlineEventNote, MdOutlineHelpOutline, MdOutlineSummarize,
} from 'react-icons/md';
import { BookingTabs, withTabPaths } from '@egreact/shared';
import Navbar from '../Navbar/Navbar';
import { useStateFields } from '../../store/hooks';
import { catchError, getAPIUrl, getAuthToken } from '../../script/util';
import './BookingFormTabs.css';

export const CREATIVE = 'creative';
export const OPS = 'ops';
export const FAQ = 'faq';
export const NOTES = 'notes';

// The form pages measure this, not the bare navbar, to know where their own
// content starts, since the tabs sit in the same fixed band under it.
export const CHROME_SELECTOR = '.booking-chrome';

export const notesPath = (bookingId) => `/booking/${encodeURIComponent(bookingId)}/notes`;

// Each form route renders its own band, so a tab click remounts it. The read
// is kept per booking so the tabs draw at once instead of being fetched again.
const formsCache = new Map();

/**
 * The booking's notes and where each of its forms lives, read once the
 * session is in place. A shared link carries its token in the address bar
 * until the page stores it, so the read waits for that.
 *
 * `data` stays null when the read is refused: the notes and the tabs are the
 * couple's own, so anyone else sees the form they opened and nothing more.
 */
export const useBookingForms = (bookingId, { refresh = false } = {}) => {
  const { isLoggedin } = useStateFields(['isLoggedin']);
  // Keyed by session too, so a different login never sees another's notes.
  const key = `${getAuthToken()}|${bookingId}`;
  const [state, setState] = useState(() => (formsCache.has(key)
    ? { loading: false, data: formsCache.get(key), error: false }
    : { loading: true, data: null, error: false }));
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setState({ loading: true, data: null, error: false });
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!isLoggedin || !bookingId) return undefined;
    if (!refresh && formsCache.has(key)) {
      setState({ loading: false, data: formsCache.get(key), error: false });
      return undefined;
    }
    let live = true;
    axios.get(getAPIUrl(`/api/booking/mom-notes/${encodeURIComponent(bookingId)}/`), {
      headers: { Authorization: getAuthToken() },
    }).then((res) => {
      // Crew read their own notes in the partner app, so this app shows the
      // tabs to the couple and to our team, who open the links from egadmin.
      const ok = res.data?.success && ['couple', 'staff'].includes(res.data.viewer);
      const data = ok ? res.data : null;
      formsCache.set(key, data);
      if (live) setState({ loading: false, data, error: false });
    }).catch((error) => {
      // The tabs are extra on these pages, so only an expired session is
      // acted on. Anything else just leaves the page without tabs. `error`
      // tells a failed read apart from a refusal, for the Notes page.
      if (error.response?.status === 401) catchError(error);
      if (live) setState({ loading: false, data: null, error: true });
    });
    return () => { live = false; };
  }, [isLoggedin, bookingId, key, refresh, attempt]);

  return { ...state, retry };
};

// `short` is what a phone shows, where all four have to fit across.
const tabsOf = (bookingId, data) => withTabPaths([
  { key: CREATIVE, label: 'Creative Form', short: 'Creative', icon: MdOutlinePalette, url: data.creative_preference?.url },
  { key: OPS, label: 'Ops Form', short: 'Ops', icon: MdOutlineEventNote, url: data.wedding_day?.url },
  { key: FAQ, label: 'What to Expect', short: 'FAQ', icon: MdOutlineHelpOutline, url: data.faq_url },
  { key: NOTES, label: 'Notes', short: 'Notes', icon: MdOutlineSummarize, path: notesPath(bookingId) },
]);

/**
 * The navbar with the booking's four tabs under it: Creative Form, Ops Form,
 * What to Expect and Notes. Each form's shared link opens on its own tab.
 *
 * Pass `forms` when the page has already read them (the Notes page does), so
 * they are not read twice.
 */
const BookingFormTabs = ({ bookingId, active, forms }) => {
  const own = useBookingForms(forms === undefined ? bookingId : null);
  const data = forms === undefined ? own.data : forms;
  const bandRef = useRef(null);

  // The band publishes its own height, and marks the page while it is up,
  // so the form screens and the Notes page start below it (see the CSS).
  // A layout effect, so on a tab switch the old band's cleanup and this
  // band's setup land in the same frame and the page does not jump.
  useLayoutEffect(() => {
    const band = bandRef.current;
    if (!band) return undefined;
    const root = document.documentElement;
    const sync = () => root.style.setProperty('--booking-chrome', `${band.offsetHeight}px`);
    sync();
    document.body.classList.add('booking-chrome-open');
    const observer = window.ResizeObserver ? new ResizeObserver(sync) : null;
    if (observer) observer.observe(band);
    return () => {
      if (observer) observer.disconnect();
      document.body.classList.remove('booking-chrome-open');
      root.style.removeProperty('--booking-chrome');
    };
  }, []);

  return (
    <div className="booking-chrome" ref={bandRef}>
      {/* No `location`: given one, the Navbar builds a tokenised URL to compare
          against, and that builder redirects to login while the token is still
          in the address bar rather than in storage. */}
      <Navbar title="Dashboard" />
      <BookingTabs
        tabs={data ? tabsOf(bookingId, data) : []}
        active={active}
        classPrefix="booking-tabs"
      />
    </div>
  );
};

export default BookingFormTabs;
